import type { LigneCachee, LigneDuPourquoi, LigneFlottante, TempsDeJAi, Vivier } from '../partage/api/types';
import { A_FAIRE_MAINTENANT, CACHEE_PAR_TES_REGLAGES, majuscule, PRIORITAIRE } from '../partage/vocabulaire';
import { ligneDesEcarts, raisonDePrioritaire } from './maintenantEnMots';

/**
 * « Maintenant » au téléphone, sans rendu (chantier `telephone-cadre`, ticket 02,
 * `design/telephone.html`, piste « Maintenant », décisions d'Igor du 2026-10-05) : quelle tâche
 * est en grand, ce que « Autre chose » montre ensuite, « Ensuite », et la liste de « Chercher une
 * autre tâche ». Le serveur classe et dit pourquoi ; ici, on choisit dans ce qu'il a rendu.
 */

type UnDebloquant = Vivier['debloquants'][number];

/** Une tâche que l'écran peut mettre en grand, et si elle vient de Prioritaire. */
export interface LigneALaUne extends LigneDuPourquoi {
  prioritaire: boolean;
}

/** Combien de lignes sous « Ensuite ». */
export const LIGNES_ENSUITE = 2;
/** Combien de lignes « Chercher une autre tâche » montre avant « Voir plus », et à chaque « Voir plus ». */
export const LIGNES_AVANT_VOIR_PLUS = 10;

/**
 * L'ordre que suit « Autre chose » : Prioritaire d'abord (décision 2 du cadrage : s'il y a du
 * prioritaire, il est en grand, même hors des réglages), puis « Pendant ce temps ». Une tâche
 * pliée sous une autre de la même échéance reste pliée : seule la tête passe en grand.
 */
export function fileDeMaintenant(vivier: Pick<Vivier, 'prioritaire' | 'pendantCeTemps'>): LigneALaUne[] {
  const vues = new Set<string>();
  const file: LigneALaUne[] = [];
  const poser = (l: LigneDuPourquoi, prioritaire: boolean) => {
    if (vues.has(l.id)) return;
    vues.add(l.id);
    file.push({ ...l, prioritaire });
  };
  for (const l of vivier.prioritaire) poser(l, true);
  for (const l of vivier.pendantCeTemps) poser(l, false);
  return file;
}

/**
 * La tâche en grand au rang donné, et « Ensuite » : les deux qui la suivent dans la file. « Autre
 * chose » avance d'un rang ; après la dernière, on revient à la première (rien n'est enregistré :
 * à la prochaine ouverture, le rang repart de zéro).
 */
export function aLaUne(file: LigneALaUne[], rang: number): { une: LigneALaUne | null; ensuite: LigneALaUne[] } {
  if (file.length === 0) return { une: null, ensuite: [] };
  const i = ((rang % file.length) + file.length) % file.length;
  const ensuite: LigneALaUne[] = [];
  for (let k = 1; k < file.length && ensuite.length < LIGNES_ENSUITE; k += 1) {
    ensuite.push(file[(i + k) % file.length]!);
  }
  return { une: file[i]!, ensuite };
}

/** Ce qui se lit au-dessus du titre en grand : « Prioritaire · Marge de 2 jours », « À faire maintenant · 10 min, sans bouger ». */
export function raisonDeLaUne(ligne: LigneALaUne): string {
  if (ligne.prioritaire) {
    const raison = raisonDePrioritaire(ligne);
    return raison ? `${PRIORITAIRE} · ${raison}` : PRIORITAIRE;
  }
  return ligne.pourquoi ? `${A_FAIRE_MAINTENANT} · ${majuscule(ligne.pourquoi)}` : A_FAIRE_MAINTENANT;
}

/** Le pourquoi d'une ligne d'« Ensuite » ou de la liste, en court. */
export function pourquoiCourt(ligne: LigneDuPourquoi & { prioritaire?: boolean }): string {
  if (ligne.prioritaire) return raisonDePrioritaire(ligne) || PRIORITAIRE;
  return ligne.pourquoi ? majuscule(ligne.pourquoi) : '';
}

/**
 * Le débloquant sous la tâche en grand : le premier des débloquants qui n'est pas elle. Le même
 * titre deux fois l'un sous l'autre ne dirait rien de plus.
 */
export function debloquantSousLaUne(debloquants: UnDebloquant[], uneId: string | null): UnDebloquant | null {
  return debloquants.find((d) => d.bulle.id !== uneId) ?? null;
}

/**
 * Tout ce que les réglages laissent passer, dans l'ordre de l'écran : Prioritaire, « Pendant ce
 * temps », puis sous l'eau (une tâche pliée juste après sa tête). Ce que « Chercher une autre
 * tâche » parcourt.
 */
export function lignesQuiPassent(vivier: Pick<Vivier, 'prioritaire' | 'pendantCeTemps' | 'sousLEau'>): LigneALaUne[] {
  const vues = new Set<string>();
  const lignes: LigneALaUne[] = [];
  const poser = (l: LigneDuPourquoi, prioritaire: boolean) => {
    if (vues.has(l.id)) return;
    vues.add(l.id);
    lignes.push({ ...l, prioritaire });
  };
  for (const l of vivier.prioritaire) poser(l, true);
  const flottantes = (l: LigneFlottante) => [l, ...l.pliees];
  for (const l of [...vivier.pendantCeTemps, ...vivier.sousLEau].flatMap(flottantes)) poser(l, false);
  return lignes;
}

/** Un titre ramené à ses lettres : sans accents ni majuscules, pour filtrer « eco » sur « École ». */
function sansAccents(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('fr-FR').trim();
}

/** Les lignes dont le titre contient le texte tapé, accents et majuscules ignorés ; tout, sans texte. */
export function filtrerParTitre<L extends { titre: string }>(lignes: L[], texte: string): L[] {
  const cherche = sansAccents(texte);
  if (!cherche) return lignes;
  return lignes.filter((l) => sansAccents(l.titre).includes(cherche));
}

/** La coupe de la liste : les `montrees` premières, et combien restent derrière « Voir plus ». */
export function coupe<L>(lignes: L[], montrees: number): { visibles: L[]; reste: number } {
  const n = Math.max(0, montrees);
  return { visibles: lignes.slice(0, n), reste: Math.max(0, lignes.length - n) };
}

/** Une tâche que « J'ai » ou « Je suis » cache, et pourquoi. */
export interface Cachee {
  ligne: LigneCachee;
  raison: string;
}

/**
 * Ce que tes réglages cachent, tel que le serveur le rend (`cachees` de `/api/maintenant` avec
 * `toutVoir=true`, chantier nettoyage-gestes, ticket 04), chacune avec sa raison en mots.
 *
 * La raison reprend les mots du débloquant (`ligneDesEcarts`) : « Il faut sortir de chez toi »
 * quand « Chez moi » est choisi et qu'il faut y aller ; « Plus long que tes 30 min » quand elle
 * dure plus que « J'ai ». `temps` : « J'ai », tel que le serveur l'a lu.
 */
export function cacheesParLesReglages(vivier: Pick<Vivier, 'cachees'>, temps: TempsDeJAi | null): Cachee[] {
  return vivier.cachees.map((ligne) => ({ ligne, raison: ligneDesEcarts(ligne.ecarts, temps) ?? CACHEE_PAR_TES_REGLAGES }));
}
